import React, {useContext, useEffect, useState, useCallback} from "react";
import {useNavigate} from "react-router-dom";
import StyleContext from "../../contexts/StyleContext";
import {useAuth} from "../../contexts/AuthContext";
import {
  adminListBlogs,
  adminCreateBlog,
  adminUpdateBlog,
  adminDeleteBlog,
  fetchBlogs,
  fetchBlog
} from "../../services/api";
import {useLoading} from "../../contexts/LoadingContext";
import {timeAgo} from "../../utils/timeAgo";
import PostEditor from "./PostEditor";
import SettingsPanel from "./SettingsPanel";
import AdminInbox from "./AdminInbox";
import ResumeEditor from "./ResumeEditor";
import Loader from "../../components/loader/Loader";
import "../blog/Blog.scss";

const emptyForm = {
  title: "",
  excerpt: "",
  content: "",
  cover_image: "",
  category: "",
  sort_order: ""
};

const AdminDashboard = () => {
  const {isDark} = useContext(StyleContext);
  const {isAuthenticated, isAdmin, token, logout} = useAuth();
  const {show: showLoader, hide: hideLoader} = useLoading();
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("posts");          // posts | inbox | resume | settings
  const [mode, setMode] = useState("list");          // list | create | edit
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  // Admin search — debounced, in-memory filter on title / excerpt / category.
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim().toLowerCase()), 220);
    return () => clearTimeout(t);
  }, [searchInput]);

  const filteredPosts = !q
    ? posts
    : posts.filter(p =>
        (p.title    || "").toLowerCase().includes(q) ||
        (p.excerpt  || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q)
      );

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) nav("/admin/login");
  }, [isAuthenticated, isAdmin, nav]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      try {
        const data = await adminListBlogs(token);
        setPosts(data.blogs || []);
      } catch (e) {
        const data = await fetchBlogs({page: 1, perPage: 100});
        setPosts(data.blogs || []);
        setError("Admin API unreachable — showing public list (read-only).");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && isAdmin && tab === "posts") load();
  }, [isAuthenticated, isAdmin, load, tab]);

  const startCreate = () => {
    setForm(emptyForm);
    setEditId(null);
    setMode("create");
    setTab("posts");
  };

  // FIX: admin-list payload omits `content` — we MUST fetch the full row
  // before opening the editor or the markdown body shows blank.
  const startEdit = async post => {
    setError("");
    setEditId(post.id);
    setMode("edit");
    setTab("posts");
    // Open immediately with whatever we already have (title/excerpt/etc),
    // then upgrade once the full row arrives. PostEditor's useEffect on
    // `initial` keeps the form in sync.
    setForm({
      title: post.title || "",
      excerpt: post.excerpt || "",
      content: post.content || "",
      cover_image: post.cover_image || "",
      category: post.category || "",
      sort_order: post.sort_order ?? ""
    });
    showLoader("Loading post");
    try {
      const data = await fetchBlog(post.id);
      const full = data?.blog || {};
      setForm({
        title: full.title || post.title || "",
        excerpt: full.excerpt || post.excerpt || "",
        content: full.content || "",
        cover_image: full.cover_image || post.cover_image || "",
        category: full.category || post.category || "",
        sort_order: full.sort_order ?? post.sort_order ?? ""
      });
    } catch (e) {
      // Live API down — fall back to whatever the list gave us; warn admin.
      setError("Couldn't load full post content — editing partial data. " + e.message);
    } finally {
      hideLoader();
    }
  };

  const savePost = async data => {
    setBusy(true);
    setError("");
    showLoader(mode === "create" ? "Publishing post" : "Saving changes");
    try {
      // Normalise empty sort_order so PHP gets a real int or NULL.
      const payload = {
        ...data,
        sort_order:
          data.sort_order === "" || data.sort_order === null || data.sort_order === undefined
            ? null
            : parseInt(data.sort_order, 10)
      };
      if (mode === "create") {
        await adminCreateBlog(payload, token);
      } else {
        await adminUpdateBlog(editId, payload, token);
      }
      setMode("list");
      setForm(emptyForm);
      setEditId(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      hideLoader();
    }
  };

  const remove = async post => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    showLoader("Deleting post");
    try {
      await adminDeleteBlog(post.id, token);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      hideLoader();
    }
  };

  if (!isAuthenticated || !isAdmin) return null;

  // ---------- Editor (full-bleed) ----------
  if (mode === "create" || mode === "edit") {
    return (
      <PostEditor
        initial={form}
        mode={mode}
        busy={busy}
        error={error}
        onSave={savePost}
        onCancel={() => { setMode("list"); setError(""); }}
      />
    );
  }

  // ---------- Dashboard shell ----------
  return (
    <main className={`admin ${isDark ? "dark" : ""}`}>
      <h1>Admin Dashboard</h1>
      <p className="admin__lead">Manage your blog posts and site settings.</p>

      <div className="admin__tabs">
        <button
          className={tab === "posts" ? "active" : ""}
          onClick={() => setTab("posts")}
        >
          Posts
        </button>
        <button
          className={tab === "inbox" ? "active" : ""}
          onClick={() => setTab("inbox")}
        >
          Inbox
        </button>
        <button
          className={tab === "resume" ? "active" : ""}
          onClick={() => setTab("resume")}
        >
          Resume
        </button>
        <button
          className={tab === "settings" ? "active" : ""}
          onClick={() => setTab("settings")}
        >
          Settings
        </button>
        <span style={{flex: 1}} />
        <button className="secondary" onClick={() => { logout(); nav("/"); }}>
          Logout
        </button>
      </div>

      {error && tab === "posts" && <p className="modal__error">{error}</p>}

      {tab === "posts" && (
        <>
          <div className="admin__bar">
            <button onClick={startCreate}>+ New Post</button>
            <div className="admin__search">
              <input
                type="search"
                className="rdf-search"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search posts by title, excerpt, or category…"
                aria-label="Search admin posts"
              />
              {searchInput && (
                <button
                  type="button"
                  className="admin__search-clear"
                  onClick={() => setSearchInput("")}
                  aria-label="Clear search"
                >×</button>
              )}
            </div>
            {q && (
              <span className="admin__search-count">
                {filteredPosts.length} of {posts.length} match
              </span>
            )}
          </div>

          {loading ? <Loader inline label="Loading posts" /> : (
            <div className="admin__table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Created</th>
                    <th>Views</th>
                    <th>Likes</th>
                    <th style={{textAlign: "right"}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPosts.map(p => (
                    <tr key={p.id}>
                      <td className="admin__title-cell">{p.title}</td>
                      <td>{p.category || "—"}</td>
                      <td>{timeAgo(p.created_at)}</td>
                      <td>{p.views || 0}</td>
                      <td>{p.likes || 0}</td>
                      <td className="admin__actions">
                        <button onClick={() => startEdit(p)}>Edit</button>
                        <button className="danger" onClick={() => remove(p)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                  {filteredPosts.length === 0 && (
                    <tr>
                      <td colSpan="6" className="admin__empty">
                        {q
                          ? `No posts match “${searchInput}”.`
                          : "No posts yet — click “+ New Post” to publish one."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "inbox"    && <AdminInbox />}
      {tab === "resume"   && <ResumeEditor />}
      {tab === "settings" && <SettingsPanel />}
    </main>
  );
};

export default AdminDashboard;
